const MAX_ATTEMPTS = 3
const BACKOFF_MS = [0, 500, 1500] // attempt 1: immediate, 2: 500ms, 3: 1500ms
const XHR_TIMEOUT_MS = 10000

const defaultWait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Sends a POST to the direct-proxy Lambda with up to MAX_ATTEMPTS retries on
 * transient failures (onerror / status 0 / timeout / abort / HTTP 5xx).
 *
 * The same requestId is used for every retry attempt so browser retry logs can
 * be grouped together even when the browser loses the response.
 *
 * Does NOT retry: HTTP 400/401/403, unexpected response bodies, parse errors.
 *
 * Resolves on first success. Rejects after all attempts are exhausted.
 *
 * Safety: each retry sends an identical payload to UniFi authorize_guest.
 * UniFi authorize_guest is idempotent for the same client_mac — a repeat call
 * resets/extends the authorization timer rather than creating a second session.
 * Retries therefore do not double-charge or create overlapping grants, but may
 * reset the 15-minute window. This is acceptable given the transient-failure
 * scenarios we are targeting.
 *
 * @param {object}   opts
 * @param {string}   opts.url        Lambda Function URL
 * @param {string}   opts.key        x-lambda-key value (not logged)
 * @param {object}   opts.payload    JSON body (preserved exactly — do not mutate)
 * @param {string}   opts.requestId  Browser log correlation ID shared across retries
 * @param {object}   opts.context    Extra fields for logs: { parkCode, siteId, clientMac, apMac }
 * @param {object}   opts.logger     datadogLogger
 * @param {string}   opts.logPrefix  Prefix for log event names (default: 'setupCheckout')
 * @param {Function} opts._wait      Injected delay fn (default: real setTimeout — override in tests)
 */
export const sendDirectProxy = async ({
  url,
  key,
  payload,
  requestId,
  context,
  logger,
  logPrefix = 'setupCheckout',
  _wait = defaultWait,
}) => {
  let lastFailureType = null
  let lastErrorCategory = null

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    if (attempt > 1) {
      await _wait(BACKOFF_MS[attempt - 1])
      logger.warn(`${logPrefix}: direct proxy retry`, {
        requestId,
        attempt: attempt - 1,   // the attempt that just failed
        nextAttempt: attempt,   // what we are about to do
        reason: lastFailureType,
        errorCategory: lastErrorCategory,
      })
    }

    logger.info(`${logPrefix}: direct proxy request:start`, {
      requestId,
      attempt,
      maxAttempts: MAX_ATTEMPTS,
      timeoutMs: XHR_TIMEOUT_MS,
      ...context,
      online: navigator.onLine,
      visibilityState: document.visibilityState,
    })

    const startMs = Date.now()
    const durationMs = () => Date.now() - startMs

    // eslint-disable-next-line no-await-in-loop
    const result = await new Promise((resolve) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url, true)
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.setRequestHeader('x-lambda-key', key)
      xhr.timeout = XHR_TIMEOUT_MS

      xhr.onload = () => {
        logger.info(`${logPrefix}: direct proxy response`, {
          requestId,
          attempt,
          status: xhr.status,
          statusText: xhr.statusText,
          responseURL: xhr.responseURL,
          durationMs: durationMs(),
          responseReceived: true,
        })

        if (xhr.status === 0) {
          // status 0 in onload: distinct from the onerror handler firing — keeps logs unambiguous.
          // status 0 is browser-side: request may have reached Lambda while browser lost the response.
          resolve({ ok: false, retryable: true, failureType: 'status0', errorCategory: 'STATUS_0', status: 0 })
          return
        }
        if (xhr.status >= 500) {
          resolve({ ok: false, retryable: true, failureType: 'http5xx', errorCategory: 'HTTP_5XX', status: xhr.status })
          return
        }
        if (xhr.status !== 200) {
          // 400/401/403 — do not retry: validation/auth errors will not resolve on retry
          resolve({ ok: false, retryable: false, failureType: 'http4xx', errorCategory: 'HTTP_4XX', status: xhr.status })
          return
        }
        try {
          const { status } = JSON.parse(xhr.responseText)
          if (status === 'ok') {
            resolve({ ok: true })
          } else {
            resolve({ ok: false, retryable: false, failureType: 'unexpected-body', errorCategory: 'UNKNOWN', responseText: xhr.responseText })
          }
        } catch (e) {
          resolve({ ok: false, retryable: false, failureType: 'parse-error', errorCategory: 'UNKNOWN', responseText: xhr.responseText, parseError: e?.message })
        }
      }

      xhr.onerror = () => {
        // xhr.status === 0 here is a browser-side observation — it does NOT mean
        // the server returned 0. The request may have reached the Lambda and succeeded
        // while the browser lost the response. The requestId in Lambda logs will
        // confirm whether the Lambda received this attempt.
        logger.error(`${logPrefix}: direct proxy error`, {
          requestId,
          attempt,
          status: xhr.status,       // likely 0 — browser-side, NOT a server response status
          readyState: xhr.readyState,
          online: navigator.onLine,
          visibilityState: document.visibilityState,
          durationMs: durationMs(),
          errorType: 'onerror',
          errorCategory: 'NETWORK_ERROR',
        })
        resolve({ ok: false, retryable: true, failureType: 'onerror', errorCategory: 'NETWORK_ERROR', status: xhr.status })
      }

      xhr.ontimeout = () => {
        // Abort the timed-out request so resources are released before the next attempt.
        // xhr.abort() on a done XHR (readyState 4, as set by the timeout event) is a no-op
        // per spec — it will NOT re-fire onabort.
        xhr.abort()
        logger.error(`${logPrefix}: direct proxy timeout`, {
          requestId,
          attempt,
          status: xhr.status,
          readyState: xhr.readyState,
          online: navigator.onLine,
          visibilityState: document.visibilityState,
          durationMs: durationMs(),
          errorType: 'timeout',
          errorCategory: 'TIMEOUT',
        })
        resolve({ ok: false, retryable: true, failureType: 'timeout', errorCategory: 'TIMEOUT', status: xhr.status })
      }

      xhr.onabort = () => {
        logger.warn(`${logPrefix}: direct proxy abort`, {
          requestId,
          attempt,
          status: xhr.status,
          readyState: xhr.readyState,
          online: navigator.onLine,
          visibilityState: document.visibilityState,
          durationMs: durationMs(),
          errorType: 'abort',
          errorCategory: 'ABORTED',
        })
        resolve({ ok: false, retryable: true, failureType: 'abort', errorCategory: 'ABORTED', status: xhr.status })
      }

      xhr.send(JSON.stringify(payload))
    })

    if (result.ok) {
      return // success — caller logs success and continues
    }

    const isLastAttempt = attempt === MAX_ATTEMPTS
    const willRetry = result.retryable && !isLastAttempt

    if (!willRetry) {
      if (result.retryable && isLastAttempt) {
        logger.error(`${logPrefix}: direct proxy all attempts failed`, {
          requestId,
          attempts: MAX_ATTEMPTS,
          finalFailureType: result.failureType,
          errorCategory: 'WIFI_AUTH_ALL_ATTEMPTS_FAILED',
          online: navigator.onLine,
          visibilityState: document.visibilityState,
        })
      } else {
        // Non-retryable: 4xx / unexpected body / parse error
        if (result.failureType === 'http4xx') {
          logger.error(`${logPrefix}: direct proxy unexpected status`, {
            requestId, attempt, status: result.status, errorCategory: 'HTTP_4XX',
          })
        } else if (result.failureType === 'unexpected-body') {
          logger.error(`${logPrefix}: direct proxy unexpected response body`, {
            requestId, attempt, responseText: result.responseText, errorCategory: 'UNKNOWN',
          })
        } else if (result.failureType === 'parse-error') {
          logger.error(`${logPrefix}: direct proxy failed to parse response`, {
            requestId, attempt, responseText: result.responseText, error: result.parseError, errorCategory: 'UNKNOWN',
          })
        }
      }
      throw new Error(result.failureType)
    }

    lastFailureType = result.failureType
    lastErrorCategory = result.errorCategory
  }
}
